import { useEffect, useRef, useCallback } from 'react';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import { useAudioStore } from '@/store/audioStore.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { getAudioContextIfExists, ensureAudioContextResumed, onAudioRecovered, playSilentKick } from '@/audio/audioProcessor.ts';
import { getBuffer, ensureDecoded } from '@/previs/audioBlobStore.ts';
import { capabilities } from '@/capabilities.ts';
import { sendToNative } from '@/bridge/embeddedBridge.ts';
import { useProjectStore } from '@/store/projectStore.ts';
import {
  getSceneBoundaries,
  getSceneIdAtTime,
  getPlaybackSceneBoundaries,
  buildClipSnapshot,
} from '@/store/audioDerived.ts';
import { getClipAbsoluteStart } from '@/store/projectDerived.ts';
import type { AudioClip } from '@/types/timeline.ts';

interface ActiveSource {
  source: AudioBufferSourceNode;
  gain: GainNode;
  clipId: number;
}

/** Schedule fade-in and fade-out gain ramps for a clip at the given buffer offset. */
function applyFades(ctx: AudioContext, gain: GainNode, clip: AudioClip, offset: number): void {
  // Fade in
  if (clip.fadeInTime > 0 && offset < clip.fadeInTime) {
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(
      clip.volume,
      ctx.currentTime + (clip.fadeInTime - offset)
    );
  }

  // Fade out
  if (clip.fadeOutTime > 0) {
    const fadeOutStart = clip.duration - clip.fadeOutTime;
    if (offset < fadeOutStart) {
      gain.gain.setValueAtTime(clip.volume, ctx.currentTime + (fadeOutStart - offset));
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + (clip.duration - offset));
    } else {
      // Already in fade-out region
      const progress = (offset - fadeOutStart) / clip.fadeOutTime;
      gain.gain.setValueAtTime(clip.volume * (1 - progress), ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + (clip.duration - offset));
    }
  }
}

/** Disconnect and remove a single ActiveSource entry. */
function teardownSource(active: ActiveSource) {
  try { active.source.stop(); } catch { /* already stopped */ }
  try { active.source.disconnect(); } catch { /* ok */ }
  try { active.gain.disconnect(); } catch { /* ok */ }
}

/**
 * Manages Web Audio playback of AudioClips synced to the timeline.
 * Starts/stops AudioBufferSourceNodes when isPlaying changes or seek occurs.
 * Mutes audio at non-1x shuttle speeds.
 *
 * Safari hardening:
 *  - Uses source.onended to auto-clean finished nodes (prevents resource leak)
 *  - Subscribes to AudioContext recovery events to re-schedule after interruption
 *  - Guards against interrupted/closed context states before creating nodes
 */
export function useAudioPlayback() {
  const isPlaying = usePlaybackStore(s => s.isPlaying);
  const currentTime = usePlaybackStore(s => s.currentTime);
  const audioClips = useAudioStore(s => s.audioClips);
  const shuttleSpeed = usePlaybackStore(s => s.shuttleSpeed);
  const isRecording = useUiStore(s => s.isRecording);

  const activeSources = useRef<ActiveSource[]>([]);
  const lastScheduledTime = useRef<number>(-1);
  const wasPlayingRef = useRef(false);

  // Stop all active sources
  const stopAll = useCallback(() => {
    for (const s of activeSources.current) {
      teardownSource(s);
    }
    activeSources.current = [];
  }, []);

  // Remove a specific source by clipId (used by onended)
  const removeSource = useCallback((clipId: number) => {
    const idx = activeSources.current.findIndex(s => s.clipId === clipId);
    if (idx !== -1) {
      const [active] = activeSources.current.splice(idx, 1);
      try { active.source.disconnect(); } catch { /* ok */ }
      try { active.gain.disconnect(); } catch { /* ok */ }
    }
  }, []);

  // Schedule a single clip, returning the ActiveSource (or null if skipped)
  const scheduleClip = useCallback((ctx: AudioContext, clip: AudioClip, time: number): ActiveSource | null => {
    if (clip.isMuted) return null;
    if (!clip.audioBuffer) {
      // Fast path: synchronous recovery from buffer cache
      const cached = getBuffer(clip.id);
      if (cached) {
        // Re-attach synchronously so this scheduling pass can use it
        console.log(`[AudioPlayback] clip ${clip.id} recovered from buffer cache`);
        clip = { ...clip, audioBuffer: cached.audioBuffer, peaks: cached.peaks };
        useAudioStore.getState().updateAudioClip(clip.id, { audioBuffer: cached.audioBuffer, peaks: cached.peaks }, { pushUndo: false });
      } else {
        // Slow path: decode from blob store
        console.warn(`[AudioPlayback] clip ${clip.id} "${clip.label}" has null audioBuffer — attempting async recovery`);
        ensureDecoded(clip.id).then(decoded => {
          if (decoded) {
            console.log(`[AudioPlayback] clip ${clip.id} async recovery succeeded`);
            useAudioStore.getState().updateAudioClip(clip.id, { audioBuffer: decoded.audioBuffer, peaks: decoded.peaks }, { pushUndo: false });
          } else {
            console.warn(`[AudioPlayback] clip ${clip.id} async recovery returned null (no blob?)`);
          }
        }).catch((err) => { console.error(`[AudioPlayback] clip ${clip.id} async recovery failed:`, err); });
        return null;
      }
    }

    const project = useProjectStore.getState().project;
    if (!project) return null;
    const buf = clip.audioBuffer!;
    const absStart = getClipAbsoluteStart(clip, project);
    const clipEnd = absStart + clip.duration;
    if (time < absStart || time >= clipEnd) return null;

    const offset = time - absStart + (clip.bufferOffset ?? 0);
    if (offset >= buf.duration) return null;

    const source = ctx.createBufferSource();
    source.buffer = buf;

    const gain = ctx.createGain();
    gain.gain.value = clip.volume;
    applyFades(ctx, gain, clip, offset);

    source.connect(gain);
    gain.connect(ctx.destination);

    // Auto-clean when the source naturally finishes (prevents Safari node pile-up)
    source.onended = () => removeSource(clip.id);

    source.start(0, offset);
    console.log(`[AudioPlayback] STARTED clip ${clip.id} offset=${offset.toFixed(2)}s ctx=${ctx.state} vol=${clip.volume} gain=${gain.gain.value} buf=${buf.duration.toFixed(2)}s/${buf.numberOfChannels}ch/${buf.sampleRate}Hz len=${buf.length}`);

    return { source, gain, clipId: clip.id };
  }, [removeSource]);

  // Schedule sources for all clips that overlap the given time.
  const scheduleAt = useCallback((ctx: AudioContext, time: number) => {
    // Suppress audio while recording so the mic doesn't pick up existing tracks
    if (useUiStore.getState().isRecording) return;
    // Don't schedule audio at non-1x speeds
    if (Math.abs(usePlaybackStore.getState().shuttleSpeed) !== 1) return;

    // Guard: context must be running (Safari can be interrupted)
    if ((ctx.state as string) !== 'running') {
      console.warn(`[AudioPlayback] scheduleAt skipped: ctx.state=${ctx.state}`);
      return;
    }

    const clips = useAudioStore.getState().audioClips;
    console.log(`[AudioPlayback] scheduleAt t=${time.toFixed(2)} clips=${clips.length} ctx.state=${ctx.state}`);
    for (const clip of clips) {
      const active = scheduleClip(ctx, clip, time);
      if (active) activeSources.current.push(active);
    }
  }, [scheduleClip]);

  // Helper: get the AudioContext, ensure it's running, then schedule.
  const resumeAndSchedule = useCallback(async (_time: number) => {
    const ctx = await ensureAudioContextResumed();
    if ((ctx.state as string) === 'running' && usePlaybackStore.getState().isPlaying) {
      scheduleAt(ctx, usePlaybackStore.getState().currentTime);
    }
  }, [scheduleAt]);

  // Convenience: stop → reschedule from current time
  const rescheduleNow = useCallback(() => {
    if (!usePlaybackStore.getState().isPlaying) return;
    stopAll();
    const time = usePlaybackStore.getState().currentTime;
    lastScheduledTime.current = time;
    resumeAndSchedule(time);
  }, [stopAll, resumeAndSchedule]);

  // ── Unity bridge: helper to send playTimeline ──────────────────────────
  const sendPlayTimeline = useCallback((time: number) => {
    if (Math.abs(usePlaybackStore.getState().shuttleSpeed) !== 1) return;
    if (useUiStore.getState().isRecording) return;
    const project = useProjectStore.getState().project;
    if (!project) return;
    const boundaries = getSceneBoundaries(project);
    const activeSceneId = useProjectStore.getState().activeSceneId;
    const isAllShotsMode = activeSceneId === null;
    const currentSceneId = isAllShotsMode
      ? getSceneIdAtTime(boundaries, time)
      : activeSceneId;
    const { windowSceneIds, sceneBoundaries } = getPlaybackSceneBoundaries(
      boundaries, currentSceneId, isAllShotsMode,
    );
    const clips = buildClipSnapshot(
      useAudioStore.getState().audioClips, boundaries, windowSceneIds, project,
    );
    sendToNative('playTimeline', {
      time,
      speed: usePlaybackStore.getState().shuttleSpeed,
      sceneId: currentSceneId,
      sceneBoundaries,
      clips,
    });
  }, []);

  // ── Unity bridge: send playTimeline/stopTimeline on play toggle ────────
  useEffect(() => {
    if (!capabilities.embeddedAudio) return;
    if (isPlaying && !wasPlayingRef.current) {
      // Stamp lastScheduledTime so the seek-detection effect (below) doesn't
      // also fire sendPlayTimeline on the same React commit cycle.
      lastScheduledTime.current = currentTime;
      sendPlayTimeline(currentTime);
    } else if (!isPlaying && wasPlayingRef.current) {
      sendToNative('stopTimeline', {});
    }
    wasPlayingRef.current = isPlaying;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);

  // ── Unity bridge: detect seeks during playback and reschedule ─────────
  useEffect(() => {
    if (!capabilities.embeddedAudio) return;
    if (!isPlaying) return;

    const prevTime = lastScheduledTime.current;
    const delta = Math.abs(currentTime - prevTime);

    // A jump of >0.5s during playback indicates a seek (scene tap, etc.)
    if (delta > 0.5) {
      sendPlayTimeline(currentTime);
    }

    lastScheduledTime.current = currentTime;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTime]);

  // ── Subscribe to AudioContext recovery (Safari interrupted → running) ───
  useEffect(() => {
    return onAudioRecovered(() => {
      if (capabilities.embeddedAudio) return;
      if (usePlaybackStore.getState().isPlaying) {
        rescheduleNow();
      }
    });
  }, [rescheduleNow]);

  // When isPlaying toggles, start or stop sources.
  // Safari can silently suspend the AudioContext between play sessions,
  // so we proactively kick it before scheduling.
  useEffect(() => {
    if (capabilities.embeddedAudio) return;
    if (isPlaying) {
      lastScheduledTime.current = currentTime;
      // Always go through ensureAudioContextResumed — it creates the context
      // if needed and resumes it. The play button click propagates a user gesture
      // through React's synthetic event system, which Safari respects.
      resumeAndSchedule(currentTime);
    } else {
      stopAll();
    }

    return () => { if (!capabilities.embeddedAudio) stopAll(); };
    // We only want to react to isPlaying toggling, not every currentTime tick
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);

  // Detect seeks and clip boundary crossings while playing
  useEffect(() => {
    if (capabilities.embeddedAudio) return;
    if (!isPlaying) return;

    const prevTime = lastScheduledTime.current;
    const delta = Math.abs(currentTime - prevTime);

    // A jump of >0.5s indicates a seek rather than normal playback advance
    if (delta > 0.5) {
      stopAll();
      lastScheduledTime.current = currentTime;
      resumeAndSchedule(currentTime);
      return;
    }

    // During normal playback, check if any new clips have entered the window
    const scheduledIds = new Set(activeSources.current.map(s => s.clipId));
    const ctx = getAudioContextIfExists();
    if (!ctx) return;
    const ctxState = ctx.state as string;

    if (ctxState === 'running') {
      const project = useProjectStore.getState().project;
      for (const clip of useAudioStore.getState().audioClips) {
        if (clip.isMuted || !clip.audioBuffer || scheduledIds.has(clip.id)) continue;
        const absStart = project ? getClipAbsoluteStart(clip, project) : 0;
        const clipEnd = absStart + clip.duration;
        if (currentTime >= absStart && currentTime < clipEnd) {
          const active = scheduleClip(ctx, clip, currentTime);
          if (active) activeSources.current.push(active);
        }
      }
    } else if (ctxState === 'suspended') {
      // Safari silently suspended — kick it and reschedule
      ctx.resume().then(() => {
        if (usePlaybackStore.getState().isPlaying) {
          rescheduleNow();
        }
      }).catch(() => {});
    }

    // Clean up sources for clips that have ended (belt-and-suspenders with onended)
    const cleanupProject = useProjectStore.getState().project;
    activeSources.current = activeSources.current.filter(active => {
      const clip = useAudioStore.getState().audioClips.find(c => c.id === active.clipId);
      if (!clip) return true; // clip removed — will be cleaned by audioClips effect
      const absStart = cleanupProject ? getClipAbsoluteStart(clip, cleanupProject) : 0;
      const clipEnd = absStart + clip.duration;
      if (currentTime >= clipEnd) {
        teardownSource(active);
        return false;
      }
      return true;
    });

    lastScheduledTime.current = currentTime;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTime]);

  // If clips change while playing (e.g. buffers finish decoding, or volume/mute
  // changes), reschedule or update gain nodes
  useEffect(() => {
    if (capabilities.embeddedAudio) return;
    if (!isPlaying) return;

    // Update gain nodes for clips that are already scheduled (volume/mute changes)
    for (const active of activeSources.current) {
      const clip = audioClips.find(c => c.id === active.clipId);
      if (clip) {
        active.gain.gain.value = clip.isMuted ? 0 : clip.volume;
      }
    }

    // Find clips that have buffers but aren't currently scheduled
    const scheduledIds = new Set(activeSources.current.map(s => s.clipId));
    const time = usePlaybackStore.getState().currentTime;
    const unscheduledProject = useProjectStore.getState().project;
    const hasUnscheduled = audioClips.some(clip => {
      if (clip.isMuted || !clip.audioBuffer || scheduledIds.has(clip.id)) return false;
      const absStart = unscheduledProject ? getClipAbsoluteStart(clip, unscheduledProject) : 0;
      const clipEnd = absStart + clip.duration;
      return time >= absStart && time < clipEnd;
    });

    if (hasUnscheduled) {
      rescheduleNow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioClips, isPlaying]);

  // When shuttle speed changes while playing, stop/start audio
  useEffect(() => {
    if (!isPlaying) return;
    if (capabilities.embeddedAudio) {
      if (Math.abs(shuttleSpeed) !== 1) sendToNative('stopTimeline', {});
      return;
    }
    if (Math.abs(shuttleSpeed) !== 1) {
      stopAll();
    } else {
      rescheduleNow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shuttleSpeed]);

  // Suppress audio playback while recording so the mic doesn't pick up existing tracks
  useEffect(() => {
    if (!isPlaying) return;
    if (capabilities.embeddedAudio) {
      if (isRecording) sendToNative('stopTimeline', {});
      return;
    }
    if (isRecording) {
      stopAll();
    } else {
      rescheduleNow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording]);

  // Warm up AudioContext on first user interaction with the page
  useEffect(() => {
    if (capabilities.embeddedAudio) return;
    const warmup = () => {
      ensureAudioContextResumed();
      document.removeEventListener('click', warmup);
      document.removeEventListener('keydown', warmup);
    };
    document.addEventListener('click', warmup, { once: true });
    document.addEventListener('keydown', warmup, { once: true });
    return () => {
      document.removeEventListener('click', warmup);
      document.removeEventListener('keydown', warmup);
    };
  }, []);

  // Keepalive: play a silent buffer every 10s while audio clips exist.
  // Prevents WebKit (especially in WKWebView) from auto-suspending an idle
  // AudioContext, which causes audio to go silent after image/TTS generation.
  const hasClips = audioClips.length > 0;
  useEffect(() => {
    if (capabilities.embeddedAudio) return;
    if (!hasClips) return;
    const interval = setInterval(() => {
      // Use getAudioContextIfExists — never create a context from a timer.
      // Safari silently mutes contexts created outside a user gesture.
      const ctx = getAudioContextIfExists();
      if (ctx && (ctx.state as string) === 'running') {
        playSilentKick(ctx);
      }
    }, 10_000);
    return () => clearInterval(interval);
  }, [hasClips]);
}
